'use client';

import { useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { trackLeadFormSubmission, trackScheduleMeetingClick } from '@/lib/analytics';
import { containsNonLatinText, NON_ENGLISH_TEXT_ERROR } from '@/lib/text-validation';

interface SoftwareQuoteFormProps {
  /** Product name */
  productName: string;
  /** Product slug for tracking */
  productSlug: string;
  /** Selected variant/plan name (optional - from parent component) */
  selectedVariantName?: string;
  /** Selected add-on names (optional - from parent component) */
  selectedAddonNames?: string[];
}

interface FormData {
  fullName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  /**
   * Seat/licence count. Required: distribution pricing is per-seat with volume
   * tiers, so a quote cannot be produced without it — asking here avoids a
   * round-trip email before any real number can be given.
   */
  seatCount: string;
  /** Commitment term. Monthly and annual differ materially in distribution. */
  term: string;
  /** Optional. Signals a switch rather than a new purchase. */
  currentProvider: string;
  /** Who provides phones today. Not a software field — see the block below. */
  phoneProvider: string;
  /** Who provides connectivity today. */
  internetProvider: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  company?: string;
  message?: string;
  seatCount?: string;
}

/** Commitment terms offered by distribution. "unsure" is allowed so an
 *  undecided buyer is never blocked from asking — we follow up instead. */
const TERM_OPTIONS = [
  { value: '', label: 'Select a term' },
  { value: 'monthly', label: 'Monthly (cancel anytime)' },
  { value: 'annual', label: 'Annual commitment' },
  { value: 'multi-year', label: 'Multi-year' },
  { value: 'unsure', label: "Not sure — advise me" },
] as const;

// Free-text fields checked for non-English (non-Latin script) input
const TEXT_FIELDS = ['fullName', 'company', 'message'] as const;

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const SCHEDULER_URL = 'https://hs.inventivehq.com/meetings/sean1324/software-page';

export default function SoftwareQuoteForm({
  productName,
  productSlug,
  selectedVariantName,
  selectedAddonNames = [],
}: SoftwareQuoteFormProps) {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    seatCount: '',
    term: '',
    currentProvider: '',
    phoneProvider: '',
    internetProvider: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Honeypot field for spam protection
  const [honeypot, setHoneypot] = useState('');

  // Track the variant name at time of submission (in case it changes while submitting)
  const [submittedVariantName, setSubmittedVariantName] = useState<string | undefined>();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    // Seat count drives the price band, so a quote is not possible without it.
    // Validate as a positive integer rather than trusting the number input,
    // which still accepts pasted text in some browsers.
    const seats = Number.parseInt(formData.seatCount, 10);
    if (!formData.seatCount.trim()) {
      newErrors.seatCount = 'Number of users or devices is required to quote';
    } else if (!Number.isFinite(seats) || seats < 1) {
      newErrors.seatCount = 'Enter a whole number of 1 or more';
    } else if (seats > 100000) {
      newErrors.seatCount = 'Please contact us directly for over 100,000 seats';
    }

    // Block non-English (non-Latin script) input in free-text fields
    for (const field of TEXT_FIELDS) {
      if (containsNonLatinText(formData[field])) {
        newErrors[field] = NON_ENGLISH_TEXT_ERROR;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if filled, it's a bot
    if (honeypot) {
      // Pretend success to not alert the bot
      setStatus('success');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setStatus('submitting');
    setErrorMessage('');
    setSubmittedVariantName(selectedVariantName);

    try {
      // Split full name into first/last for HubSpot
      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      const response = await fetch('/api/software-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          // Distribution inputs — seats drive the price band, term drives the
          // rate card, current provider flags a switch vs a new purchase.
          seatCount: Number.parseInt(formData.seatCount, 10),
          term: formData.term || undefined,
          currentProvider: formData.currentProvider.trim() || undefined,
          // Network & comms attach. Costs the buyer two optional fields and is
          // the only warm US intent we see for a business that pays residual
          // commission on connectivity and UCaaS.
          phoneProvider: formData.phoneProvider.trim() || undefined,
          internetProvider: formData.internetProvider.trim() || undefined,
          // Where the request came from, so KB-sourced quotes are attributable
          // separately from ones that started on the product page.
          // Path *and* query: KB quote CTAs carry utm_content=<article slug>,
          // which is the only record of which article produced the lead. Sending
          // pathname alone would attribute every quote to /software/<product>.
          sourcePage:
            typeof window !== 'undefined'
              ? window.location.pathname + window.location.search
              : undefined,
          productName,
          productSlug,
          variantName: selectedVariantName,
          addonNames: selectedAddonNames.length > 0 ? selectedAddonNames : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit form');
      }

      setStatus('success');
      setFormData({
        fullName: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });

      // Track conversion in GA4
      trackLeadFormSubmission('Software Quote Form', productSlug);
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Warn immediately if the user types non-English (non-Latin script) characters
    if ((TEXT_FIELDS as readonly string[]).includes(name) && containsNonLatinText(value)) {
      setErrors((prev) => ({ ...prev, [name]: NON_ENGLISH_TEXT_ERROR }));
      return;
    }

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full mb-6">
          <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Quote Request Received!</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          We&apos;ll get back to you within 1 business day about{' '}
          <strong>{productName}</strong>
          {submittedVariantName && <span> ({submittedVariantName})</span>}.
        </p>

        {/* Schedule Now CTA */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 dark:border-blue-900 rounded-xl p-6 mb-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Want to discuss your requirements now? Schedule a call with our team.
          </p>
          <a
            href={SCHEDULER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackScheduleMeetingClick('software_quote_form_success')}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Schedule a Call Now
          </a>
        </div>

        <button
          onClick={() => setStatus('idle')}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} name="Software Quote Request" className="space-y-5">
      {/* Product & Selected Plan Display */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-100 dark:border-blue-900">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product</p>
        <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{productName}</p>
        {selectedVariantName && (
          <p className="text-blue-600 dark:text-blue-400 font-medium mt-1">
            Plan: {selectedVariantName}
          </p>
        )}
        {selectedAddonNames.length > 0 && (
          <p className="text-amber-700 dark:text-amber-300 font-medium mt-1 text-sm">
            Add-ons: {selectedAddonNames.join(', ')}
          </p>
        )}
        {!selectedVariantName && selectedAddonNames.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Select a plan on the left, or submit for general inquiries
          </p>
        )}
      </div>

      {/* Honeypot field - hidden from users, visible to bots */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Full Name<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.fullName
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
          } focus:outline-none focus:ring-2 transition-colors`}
          placeholder="John Doe"
        />
        {errors.fullName && (
          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.fullName}
          </p>
        )}
      </div>

      {/* Work Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Work Email<span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.email
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
          } focus:outline-none focus:ring-2 transition-colors`}
          placeholder="john@company.com"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.email}
          </p>
        )}
      </div>

      {/* Company */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Company Name<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.company
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
          } focus:outline-none focus:ring-2 transition-colors`}
          placeholder="Acme Inc."
        />
        {errors.company && (
          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.company}
          </p>
        )}
      </div>

      {/* Phone - Optional */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Phone Number <span className="text-gray-400 font-normal">(Optional)</span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors"
          placeholder="+1 (555) 123-4567"
        />
      </div>

      {/* Seat count + term. Both feed distribution pricing directly — without a
          quantity there is no quote to give, so seats are required while term
          allows "not sure" rather than blocking an undecided buyer. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="seatCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Users or Devices <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="seatCount"
            name="seatCount"
            min={1}
            step={1}
            inputMode="numeric"
            value={formData.seatCount}
            onChange={handleChange}
            aria-invalid={errors.seatCount ? 'true' : undefined}
            aria-describedby={errors.seatCount ? 'seatCount-error' : undefined}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.seatCount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            } focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors`}
            placeholder="e.g. 75"
          />
          {errors.seatCount && (
            <p id="seatCount-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
              {errors.seatCount}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="term" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Term <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <select
            id="term"
            name="term"
            value={formData.term}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors bg-white dark:bg-gray-900"
          >
            {TERM_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Current provider — optional, but it distinguishes a switch from a new
          purchase, which changes both the pricing motion and the migration talk. */}
      <div>
        <label htmlFor="currentProvider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Current Solution <span className="text-gray-400 font-normal">(Optional)</span>
        </label>
        <input
          type="text"
          id="currentProvider"
          name="currentProvider"
          value={formData.currentProvider}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors"
          placeholder="What are you using today?"
        />
      </div>

      {/* Phone and internet providers.
          These have nothing to do with the software being quoted, and that is
          the point: this form already has a US business telling us its seat
          count and current stack, which is the only warm connectivity intent we
          see. Both are optional and neither gates the quote — a buyer who
          ignores them still gets priced normally. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phoneProvider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone system <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            type="text"
            id="phoneProvider"
            name="phoneProvider"
            value={formData.phoneProvider}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors"
            placeholder="Who provides your phones?"
          />
        </div>
        <div>
          <label htmlFor="internetProvider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Internet provider <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            type="text"
            id="internetProvider"
            name="internetProvider"
            value={formData.internetProvider}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors"
            placeholder="Who provides your internet?"
          />
        </div>
      </div>

      {/* Message - Optional */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Message <span className="text-gray-400 font-normal">(Optional)</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          className={`w-full px-4 py-3 rounded-lg border ${
            errors.message
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
          } focus:outline-none focus:ring-2 transition-colors resize-y`}
          placeholder="Number of users, timeline, specific requirements..."
        />
        {errors.message && (
          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.message}
          </p>
        )}
      </div>

      {/* Error message */}
      {status === 'error' && (
        <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 dark:text-red-300 font-medium">Failed to submit</p>
            <p className="text-red-600 dark:text-red-400 text-sm">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Submit button */}
      <div>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full px-8 py-4 bg-amber-400 hover:bg-amber-300 disabled:bg-gray-400 text-gray-900 dark:text-gray-100 font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-md"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Get my price'
          )}
        </button>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
          Response guaranteed within 1 business day. No credit card required.
        </p>
      </div>
    </form>
  );
}
